import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Plus, X, ZoomIn, Loader2 } from 'lucide-react';

const ImageUploadBox = forwardRef(({
  uploadedImage,
  setUploadedImage,
  setUploadedImageUrl,
  onPreviewImage,
  isLoggedIn,
  onLoginRequired,
  uploadApiUrl = import.meta.env?.VITE_IMAGE_UPLOAD_API || 'https://img.scdn.io/api/v1.php'
}, ref) => {
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrorMsg('格式不支持');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setErrorMsg('文件过大(>10M)');
      return false;
    }
    return true;
  };

  const uploadFile = useCallback((file) => {
    if (!isLoggedIn) {
      onLoginRequired();
      return;
    }

    if (!validateFile(file)) return;

    setErrorMsg('');
    setIsUploading(true);
    setProgress(0);

    // Create local preview
    const previewUrl = URL.createObjectURL(file);
    setUploadedImage(previewUrl);

    // Upload
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('image', file);
    formData.append('cdn_domain', 'img.scdn.io');

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        setProgress(percent);
      }
    };

    xhr.onload = () => {
      setIsUploading(false);
      if (xhr.status === 200) {
        try {
          const json = JSON.parse(xhr.responseText);
          const url = json?.url || json?.data?.url || json?.link || '';
          if (url) {
            setUploadedImageUrl(url);
            setProgress(100);
          } else {
            setErrorMsg('上传失败');
          }
        } catch (e) {
          setErrorMsg('响应错误');
        }
      } else {
        setErrorMsg('上传失败');
      }
    };

    xhr.onerror = () => {
      setIsUploading(false);
      setErrorMsg('网络错误');
    };

    xhr.open('POST', uploadApiUrl, true);
    xhr.send(formData);

  }, [isLoggedIn, onLoginRequired, setUploadedImage, setUploadedImageUrl, uploadApiUrl]);

  useImperativeHandle(ref, () => ({
    uploadFile: (file) => {
      uploadFile(file);
    }
  }));

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      uploadFile(e.dataTransfer.files[0]);
    }
  }, [uploadFile]);

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      uploadFile(e.target.files[0]);
    }
  };

  const handlePaste = useCallback((e) => {
    // Only handle paste if this component or its children are focused, 
    // OR if the mouse is over the component (handled by ref check in effect? No, paste event targets the focused element)
    // Actually, user wants "Support pasting (Ctrl+V) from clipboard directly".
    // If we bind to the div, it needs focus.
    // If we bind to window, we need to make sure we don't interfere with other inputs.
    // For now, let's rely on the div being focusable and receiving the event, 
    // AND check if the user is hovering? No, paste usually goes to focused element.
    // We will make the div focusable.

    if (e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0) {
      const file = e.clipboardData.files[0];
      if (file.type.startsWith('image/')) {
        e.preventDefault();
        uploadFile(file);
      }
    }
  }, [uploadFile]);

  // Add paste listener to the box
  useEffect(() => {
    const currentBox = boxRef.current;
    if (currentBox) {
      currentBox.addEventListener('paste', handlePaste);
    }
    return () => {
      if (currentBox) {
        currentBox.removeEventListener('paste', handlePaste);
      }
    };
  }, [handlePaste]);

  const clearImage = (e) => {
    e.stopPropagation();
    if (uploadedImage?.startsWith('blob:')) {
      URL.revokeObjectURL(uploadedImage);
    }
    setUploadedImage(null);
    setUploadedImageUrl('');
    setProgress(0);
    setErrorMsg('');
  };


  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Reset states when uploadedImage changes
  useEffect(() => {
    if (uploadedImage) {
      setHasError(false);
      // Only set loading if it's a remote URL (not starting with blob:)
      setIsLoading(!uploadedImage.startsWith('blob:'));
    } else {
      setIsLoading(false);
      setHasError(false);
    }
  }, [uploadedImage]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div
      ref={boxRef}
      className={`relative w-28 h-28 flex-shrink-0 rounded-2xl border-2 border-dashed transition-all overflow-hidden flex flex-col items-center justify-center outline-none group/uploadbox ${dragActive ? 'border-blue-500 bg-blue-50 scale-105 shadow-xl shadow-blue-100 ring-4 ring-blue-50' : 'border-gray-200 hover:border-blue-300 bg-gray-50/50 hover:bg-white'
        } ${errorMsg ? 'border-red-400' : ''}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => !uploadedImage && inputRef.current?.click()}
      tabIndex="0" // Make focusable for paste
      title="点击、拖拽或粘贴(Ctrl+V)上传参考图片"
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/png, image/jpeg, image/gif, image/webp"
        onChange={handleChange}
      />

      {uploadedImage ? (
        <div className="relative w-full h-full group/image bg-gray-50 flex items-center justify-center">
          {/* Loading State */}
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500 mb-1" />
              <span className="text-[10px] text-gray-400">加载中...</span>
            </div>
          )}

          {/* Error State */}
          {hasError ? (
            <div className="flex flex-col items-center justify-center text-center p-1 w-full h-full">
              <span className="text-red-400 text-xs mb-1">加载失败</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLoading(true);
                  setHasError(false);
                  // Retry logic: force reload by appending time param if it's a URL
                  const currentSrc = uploadedImage;
                  setUploadedImage(null);
                  setTimeout(() => setUploadedImage(currentSrc), 100);
                }}
                className="text-[10px] text-blue-500 hover:underline"
              >
                重试
              </button>
            </div>
          ) : (
            <img
              src={uploadedImage}
              alt="Preview"
              className={`w-full h-full object-contain transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
            />
          )}

          {/* Progress Mask */}
          {isUploading && (
            <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center z-20 backdrop-blur-sm">
              <div className="relative w-10 h-10 mb-1">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                  <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#ffffff" strokeOpacity="0.2" strokeWidth="4" />
                  <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#3b82f6" strokeWidth="4" strokeDasharray={`${progress}, 100`} />
                </svg>
              </div>
              <span className="text-white text-[10px] font-bold">{progress}%</span>
            </div>
          )}

          {/* Hover Actions (Zoom) */}
          {!isUploading && (
            <div
              className="absolute inset-0 bg-black/0 group-hover/image:bg-black/20 transition-colors flex items-center justify-center cursor-pointer z-10"
              onClick={() => onPreviewImage && onPreviewImage(uploadedImage)}
            >
              <ZoomIn className="w-7 h-7 text-white opacity-0 group-hover/image:opacity-100 transition-opacity drop-shadow-md" />
            </div>
          )}

          {/* Delete Button */}
          <div
            className="absolute top-0 right-0 p-1.5 bg-black/40 hover:bg-red-500 rounded-bl-xl transition-colors z-30 cursor-pointer"
            onClick={clearImage}
          >
            <X className="w-3.5 h-3.5 text-white" />
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-gray-400 pointer-events-none p-2 text-center">
          {isUploading ? (
            <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
          ) : (
            <>
              <div className={`p-2 rounded-full mb-1.5 transition-all ${dragActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 group-hover/uploadbox:bg-blue-50 group-hover/uploadbox:text-blue-500'}`}>
                <Plus className="w-6 h-6" />
              </div>
              <p className={`text-[11px] font-black leading-tight mb-1 transition-colors ${dragActive ? 'text-blue-600' : 'text-gray-500 group-hover/uploadbox:text-blue-500'}`}>
                参考图片
              </p>
              <span className="text-[9px] text-gray-400/80 font-medium">点击/粘贴</span>
            </>
          )}
        </div>
      )}

      {errorMsg && (
        <div className="absolute bottom-0 left-0 right-0 bg-red-500/90 text-white text-[9px] px-1 py-1 text-center truncate z-40 font-bold">
          {errorMsg}
        </div>
      )}
    </div>
  );
});

export default ImageUploadBox;
